import React from 'react';
import KPICard from '../../components/shared/KPICard';
import StatusPill from '../../components/shared/StatusPill';
import SectionDivider from '../../components/shared/SectionDivider';
import { stateCompliance, nationalKPIs } from '../../data/mockData';

interface DeviceHealthRow {
  title: string;
  subtitle: string;
  status: 'urgent' | 'review' | 'normal' | 'future';
  isFuture?: boolean;
}

const deviceHealthRows: DeviceHealthRow[] = [
  { title: 'Certificate Expiry', subtitle: '67 devices expiring in <30 days', status: 'urgent' },
  { title: 'Firmware Outdated', subtitle: '342 devices on version <2.3', status: 'review' },
  { title: 'OTP Mismatches', subtitle: '12 devices with key mismatch', status: 'review' },
  { title: 'Avg Data Latency', subtitle: '2.4 seconds — within threshold', status: 'normal' },
  { title: 'AI Forecast Engine', subtitle: 'Generation forecasting module', status: 'future', isFuture: true },
];

export default function NationalCompliance() {
  return (
    <div className="p-6 space-y-6">
      {/* KPIs */}
      <div className="flex gap-4">
        <KPICard title="Data Transmission" value={`${nationalKPIs.dataTransmissionPercent}%`} subtext="Above 95% threshold" subtextColor="green" />
        <KPICard title="MNRE Compliance Score" value={`${nationalKPIs.mnreComplianceScore}/100`} subtext="National average" />
        <KPICard title="Outdated Firmware" value={nationalKPIs.outdatedFirmware} subtext="Devices below v2.3" subtextColor="amber" bgColor="bg-orange-50" />
        <KPICard title="Certs Expiring <30d" value={nationalKPIs.certsExpiring} subtext="Urgent renewal needed" subtextColor="red" bgColor="bg-red-50" />
        <KPICard title="Platform Uptime" value={`${nationalKPIs.platformUptime}%`} subtext="Last 30 days" subtextColor="green" />
      </div>

      <div className="flex gap-4">
        {/* State compliance progress bars */}
        <div className="flex-1 bg-white rounded-card border border-border shadow-card p-4">
          <SectionDivider title="State-wise Compliance" subtitle="Data transmission & reporting" />
          <div className="space-y-4">
            {stateCompliance.map(item => (
              <div key={item.state} className="flex items-center gap-3">
                <span className="text-xs text-text-secondary w-32 flex-shrink-0">{item.state}</span>
                <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${item.status === 'green' ? 'bg-success' : item.status === 'amber' ? 'bg-warning' : 'bg-danger'}`}
                    style={{ width: `${item.percent}%` }}
                  />
                </div>
                <span className={`text-xs font-semibold w-12 text-right ${item.status === 'green' ? 'text-success' : item.status === 'amber' ? 'text-warning' : 'text-danger'}`}>
                  {item.percent}%
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Device health summary */}
        <div className="w-80 flex-shrink-0 bg-white rounded-card border border-border shadow-card p-4">
          <SectionDivider title="Device Health Summary" subtitle="Platform-wide diagnostics" />
          <div className="space-y-3">
            {deviceHealthRows.map((row, i) => (
              <div
                key={i}
                className={`flex items-start justify-between gap-2 p-3 rounded-input border ${row.isFuture ? 'border-dashed border-purple' : 'border-border'}`}
              >
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-text-primary">{row.title}</p>
                  <p className="text-xs text-text-secondary mt-0.5">{row.subtitle}</p>
                </div>
                <StatusPill
                  status={row.status}
                  label={row.isFuture ? 'Optional' : undefined}
                  dashed={row.isFuture}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
